/**
 * walkHereState — module-singleton state for the right-click "Walk here" flow
 * (ENG-1050). Same shape as waypointPickerStore: a plain `state`, a `Set` of
 * listeners, `emit()`. Not React state — the route preview is drawn by an
 * imperative THREE component and the pill by a DOM one, and both must read the
 * same phase without either owning it.
 *
 * The phase IS the user feedback contract. Every one of these is visible
 * somewhere (WalkHereIndicator pill + WalkHereRoute preview); there is no silent
 * phase, and no phase that means "something is happening but nothing shows".
 *
 *   idle       nothing in flight
 *   planning   route planned, preview drawn, request not yet sent
 *   generating kimodo is generating (seconds — Doherty: progress is mandatory)
 *   walking    the clip is playing on the avatar
 *   arrived    landed; brief completion beat before idle
 *   error      failed, with the real message; persists until dismissed
 *
 * `requestId` increments on every start(): a superseded or cancelled request
 * must not be able to drive the pill after a newer one took over, so the
 * orchestrator checks the id back before every transition.
 */
const IDLE = {
  phase: 'idle',
  message: null,
  route: null,        // { mode, path, segments, length } — the planned polyline
  destination: null,  // [x, y, z] clamped goal
  distance: 0,        // route metres actually requested
  clamped: false,     // goal was pulled in to stay under the duration cap
  requestId: 0,
}

let state = IDLE
const listeners = new Set()
function emit() { for (const fn of listeners) fn() }
function set(patch) { state = { ...state, ...patch }; emit() }

export const walkHereState = {
  getState() { return state },
  subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn) },

  /** Begin a request: draws the preview and takes the next requestId. */
  start({ route, destination, distance, clamped }) {
    const requestId = state.requestId + 1
    set({ phase: 'planning', message: null, route, destination, distance, clamped, requestId })
    return requestId
  },

  /** Advance a phase, but only if `requestId` is still the current request. */
  advance(requestId, phase, message = null) {
    if (requestId !== state.requestId) return false
    set({ phase, message })
    return true
  },

  /** Terminal failure. Also honours requestId so a stale rejection stays quiet. */
  fail(requestId, message) {
    if (requestId !== state.requestId) return false
    set({ phase: 'error', message: String(message) })
    return true
  },

  /** Clear back to idle — dismissing an error, or the completion beat expiring. */
  reset() { state = { ...IDLE, requestId: state.requestId }; emit() },
}

if (typeof window !== 'undefined') window.__WALK_HERE__ = walkHereState
